import { useState } from 'react'
import type { UseUserResult } from '../../hooks/useUser'
import type { ActivityLevel, Goal } from '../../types'
import { ACTIVITY_LABELS, computeTargets } from '../../utils/targets'
import { saveUserData, setPassword as apiSetPassword, updateSettings, ApiHttpError } from '../../utils/api'
import { showNotification } from '../../utils/notifications'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const GOALS: { key: Goal; label: string; emoji: string }[] = [
  { key: 'lose', label: 'Lose weight', emoji: '📉' },
  { key: 'maintain', label: 'Maintain', emoji: '⚖️' },
  { key: 'gain', label: 'Gain / build', emoji: '📈' },
]

export default function OnboardingWizard({
  userId,
  user,
  onDone,
}: {
  userId: string
  user: UseUserResult
  onDone: () => void
}) {
  const [step, setStep] = useState(0)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  // Goals
  const [name, setName] = useState('')
  const [goal, setGoal] = useState<Goal | ''>('')
  // Details
  const [weight, setWeight] = useState('')
  const [heightCm, setHeightCm] = useState('')
  const [dob, setDob] = useState('')
  const [gender, setGender] = useState<'' | 'male' | 'female'>('')
  const [activityLevel, setActivityLevel] = useState<ActivityLevel | ''>('')
  // Secure
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [email, setEmail] = useState('')

  const labelCls = 'mb-1 block text-sm text-[var(--text-secondary)]'

  const preview = computeTargets({
    ...user.data,
    weight: Number(weight) || 0,
    gender,
    heightCm: Number(heightCm) || undefined,
    dob: dob || undefined,
    activityLevel: activityLevel || undefined,
    goal: goal || undefined,
  })

  const finish = async () => {
    setError('')
    if (password) {
      if (password.length < 8) return setError('Password must be at least 8 characters')
      if (password !== confirm) return setError('Passwords do not match')
    }
    if (email && !EMAIL_RE.test(email.trim())) return setError('Please enter a valid email')

    setBusy(true)
    try {
      const record = {
        ...user.data,
        name: name.trim(),
        weight: Number(weight) || 0,
        gender,
        heightCm: Number(heightCm) || undefined,
        dob: dob || undefined,
        activityLevel: activityLevel || undefined,
        goal: goal || undefined,
      }
      user.updateData(record)
      // Persist the record first so the password/settings endpoints have something to attach to.
      await saveUserData(userId, record)

      if (password) {
        await apiSetPassword(userId, password, undefined, 'on_load')
        user.cachePassword(password)
        sessionStorage.setItem(`wlns:unlocked:${userId}`, '1')
      }
      if (email.trim()) {
        await updateSettings(userId, { backupEmail: email.trim(), currentPassword: password || undefined })
      }
      user.reload()
      showNotification('You’re all set!')
      onDone()
    } catch (e) {
      setError(e instanceof ApiHttpError ? e.message : 'Something went wrong — you can finish setup later in Settings')
      setBusy(false)
    }
  }

  const Dots = () => (
    <div className="mb-6 flex justify-center gap-2">
      {[0, 1, 2].map((i) => (
        <span key={i} className={`h-1.5 w-8 rounded-full ${i <= step ? 'bg-[var(--accent-teal)]' : 'bg-[var(--border)]'}`} />
      ))}
    </div>
  )

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-[var(--bg-primary)] p-4">
      <div className="w-full max-w-md py-8">
        <div className="mb-6 text-center">
          <span className="font-display text-2xl">
            <span className="font-extrabold uppercase tracking-tight text-[var(--text-primary)]">WLNS</span>
            <span className="text-[0.8em] font-light text-[var(--text-secondary)]">.app</span>
          </span>
        </div>
        <Dots />

        {step === 0 && (
          <div className="space-y-5">
            <div className="text-center">
              <h1 className="font-display text-2xl font-bold">Welcome 👋</h1>
              <p className="mt-2 text-[var(--text-secondary)]">Let’s tailor things to you. First — what are you here to do?</p>
            </div>
            <div>
              <label className={labelCls}>What should we call you? (optional)</label>
              <input className="w-full" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" />
            </div>
            <div>
              <label className={labelCls}>Your goal</label>
              <div className="grid grid-cols-3 gap-2">
                {GOALS.map((g) => (
                  <button
                    key={g.key}
                    onClick={() => setGoal(g.key)}
                    className={`rounded-xl border p-3 text-center text-sm transition-colors ${
                      goal === g.key ? 'border-[var(--accent-teal)] bg-[var(--bg-card)]' : 'border-[var(--border)] hover:bg-[var(--bg-card)]'
                    }`}
                  >
                    <div className="text-xl">{g.emoji}</div>
                    <div className="mt-1">{g.label}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4">
            <div className="text-center">
              <h1 className="font-display text-2xl font-bold">A bit about you</h1>
              <p className="mt-2 text-sm text-[var(--text-secondary)]">Powers your calorie &amp; nutrition targets. All optional — you can edit later.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Weight (kg)</label>
                <input className="w-full" type="number" inputMode="decimal" value={weight} onChange={(e) => setWeight(e.target.value)} />
              </div>
              <div>
                <label className={labelCls}>Height (cm)</label>
                <input className="w-full" type="number" inputMode="decimal" value={heightCm} onChange={(e) => setHeightCm(e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Date of birth</label>
                <input className="w-full" type="date" value={dob} onChange={(e) => setDob(e.target.value)} />
              </div>
              <div>
                <label className={labelCls}>Sex</label>
                <select className="w-full" value={gender} onChange={(e) => setGender(e.target.value as '' | 'male' | 'female')}>
                  <option value="">Select…</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </div>
            </div>
            <div>
              <label className={labelCls}>Activity level</label>
              <select className="w-full" value={activityLevel} onChange={(e) => setActivityLevel(e.target.value as ActivityLevel | '')}>
                <option value="">Select…</option>
                {(Object.keys(ACTIVITY_LABELS) as ActivityLevel[]).map((k) => (
                  <option key={k} value={k}>{ACTIVITY_LABELS[k]}</option>
                ))}
              </select>
            </div>
            {preview && (
              <div className="card text-sm">
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div><div className="font-bold">{preview.bmr}</div><div className="text-[10px] uppercase text-[var(--text-secondary)]">BMR</div></div>
                  <div><div className="font-bold">{preview.tdee}</div><div className="text-[10px] uppercase text-[var(--text-secondary)]">TDEE</div></div>
                  <div><div className="font-bold text-[var(--accent-teal)]">{preview.calorieTarget}</div><div className="text-[10px] uppercase text-[var(--text-secondary)]">Target</div></div>
                </div>
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <div className="text-center">
              <h1 className="font-display text-2xl font-bold">Secure &amp; recover</h1>
              <p className="mt-2 text-sm text-[var(--text-secondary)]">
                Your log lives at a private key URL. Add a password and a backup email so you can lock it and recover
                access if you lose the link. Both optional, but recommended.
              </p>
            </div>
            <div>
              <label className={labelCls}>Backup email (for recovery)</label>
              <input className="w-full" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Password</label>
                <input className="w-full" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="8+ characters" />
              </div>
              <div>
                <label className={labelCls}>Confirm</label>
                <input className="w-full" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
              </div>
            </div>
            <p className="text-xs text-[var(--text-secondary)]">
              💡 With a backup email you can reset a forgotten password or recover your key. Without it, a lost key can’t
              be recovered.
            </p>
          </div>
        )}

        {error && <p className="mt-4 text-center text-sm text-[var(--bac-danger)]">{error}</p>}

        <div className="mt-6 flex items-center gap-3">
          {step > 0 && (
            <button onClick={() => setStep((s) => s - 1)} disabled={busy} className="btn btn-secondary">
              Back
            </button>
          )}
          {step < 2 ? (
            <button onClick={() => setStep((s) => s + 1)} className="btn btn-primary flex-1">
              Continue
            </button>
          ) : (
            <button onClick={finish} disabled={busy} className="btn btn-primary flex-1">
              {busy ? 'Setting up…' : 'Finish setup'}
            </button>
          )}
        </div>
        <button onClick={onDone} disabled={busy} className="mt-4 w-full text-center text-xs text-[var(--text-secondary)] hover:text-[var(--text-primary)]">
          Skip for now
        </button>
      </div>
    </div>
  )
}
